import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Button, Modal } from '@spirit-testing/component-library';
import { startWith } from 'rxjs';
import { ProjectIntegration, ProviderType } from '../../models/project-integration.model';
import { CreateBugFromStepResultRequest, CreatedBug } from '../../models/test-run.model';
import { TestRunService } from '../../services/test-run.service';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { providerLabel } from '../../shared/provider-meta';
import { TRACKER_ERROR_KEYS, TrackerError, mapBugCreateError } from '../../shared/tracker-error';
import {
  BugTargetChoice,
  NO_BUG_TARGET_CHOICE,
  chooseBugProvider,
  resolveBugTargets,
} from '../bug-target-picker/bug-target-choice';
import { BugTargetPickerComponent } from '../bug-target-picker/bug-target-picker.component';
import { ModalCloseGuardDirective, ModalCloseOrigin } from '../modal-close-guard/modal-close-guard.directive';
import { ModalEscapeGuardDirective } from '../modal-escape-guard/modal-escape-guard.directive';

/** Wozu der Vorgang entsteht; {@code provider} ist der Tracker, dessen Knopf gedrueckt wurde. */
export interface BugticketDialogRequest {
  stepResultId: string;
  defaultTitle: string;
  sourceRef: string;
  provider: ProviderType | null;
}

/**
 * Legt den Vorgang im Tracker an (anders als `defect-erfassen-dialog`, der einen internen Fehler
 * anlegt). Offen ist der Dialog, solange {@link request} gesetzt ist; auch nach dem Anlegen, denn
 * erst der Erfolgszustand zeigt den Link.
 */
@Component({
  selector: 'app-bugticket-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BugTargetPickerComponent,
    Button,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    Modal,
    ModalCloseGuardDirective,
    ModalEscapeGuardDirective,
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
  ],
  templateUrl: './bugticket-dialog.component.html',
  styleUrl: './bugticket-dialog.component.scss',
})
export class BugticketDialogComponent {
  private readonly testRunService = inject(TestRunService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly targets = input.required<readonly ProjectIntegration[]>();
  readonly request = input<BugticketDialogRequest | null>(null);

  readonly created = output<CreatedBug>();
  readonly closed = output<void>();

  readonly fieldLengths = FIELD_LENGTHS;
  readonly toAccountKey = TRACKER_ERROR_KEYS.toAccount;

  readonly form = new FormGroup({
    title: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)],
    }),
    description: new FormControl('', {
      nonNullable: true,
      validators: Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX),
    }),
  });

  readonly open = computed(() => !!this.request());
  readonly choice = signal<BugTargetChoice>(NO_BUG_TARGET_CHOICE);
  readonly submitting = signal(false);
  readonly error = signal<TrackerError | null>(null);
  readonly createdBug = signal<CreatedBug | null>(null);

  private readonly options = computed(() => resolveBugTargets(this.targets(), this.choice()));
  readonly offersChoice = computed(() => this.options().offersChoice);
  readonly trackerLabel = computed(() => {
    const provider = this.createdBug()?.providerType ?? this.options().provider;
    return provider ? providerLabel(provider) : '';
  });

  private readonly formValue = toSignal(this.form.valueChanges.pipe(startWith(this.form.getRawValue())), {
    requireSync: true,
  });

  readonly canSubmit = computed(
    () => !this.submitting() && !!this.formValue().title?.trim() && this.form.valid && this.options().target !== null,
  );

  private readonly doneButton = viewChild('doneButton', { read: ElementRef<HTMLElement> });

  constructor() {
    effect(() => {
      const request = this.request();
      if (!request) return;
      this.form.reset({ title: request.defaultTitle.slice(0, FIELD_LENGTHS.NAME_MAX), description: '' });
      this.choice.set(request.provider ? chooseBugProvider(request.provider) : NO_BUG_TARGET_CHOICE);
      this.error.set(null);
      this.createdBug.set(null);
      this.submitting.set(false);
    });
    // Der gedrueckte Knopf verschwindet mit dem Erfolg; der Fokus bliebe sonst nirgends im Dialog.
    effect(() => this.doneButton()?.nativeElement.focus());
  }

  submit(): void {
    const stepResultId = this.request()?.stepResultId;
    if (!stepResultId || !this.canSubmit()) return;
    const target = this.options().target;
    this.submitting.set(true);
    this.error.set(null);
    this.testRunService
      .createBugFromStepResult(this.projectId(), stepResultId, this.body(target))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (bug) => {
          this.submitting.set(false);
          this.createdBug.set(bug);
          this.created.emit(bug);
        },
        error: (failure: HttpErrorResponse) => {
          this.submitting.set(false);
          this.error.set(bugCreateError(failure, target?.boardUrl ?? null));
        },
      });
  }

  close(): void {
    this.closed.emit();
  }

  /**
   * Waehrend des Anlegens bleibt der Dialog offen, sonst ginge eine Absage des Trackers ungesehen
   * verloren. Ein Fehlklick neben den Dialog verwirft keine Eingaben; Esc, X und Abbrechen schon.
   */
  readonly canClose = (origin: ModalCloseOrigin): boolean => {
    if (this.submitting()) return false;
    return origin !== 'backdrop' || !this.form.dirty || !!this.createdBug();
  };

  private body(target: ProjectIntegration | null): CreateBugFromStepResultRequest {
    const value = this.form.getRawValue();
    const description = value.description.trim();
    return {
      subject: value.title.trim(),
      // Ohne Text schreibt der Server seine Standardbeschreibung aus dem Testergebnis.
      ...(description ? { description } : {}),
      ...(target ? { integrationId: target.id } : {}),
    };
  }
}

/**
 * Absagen des Trackers mit eigenem Code gehen vor (NEX-1354), deshalb heisst nur ein 409 ohne Code
 * „schon verknuepft“. Ein 412 hat zwei Ursachen: keine aktive Anbindung (NEX-1117) oder kein
 * persoenliches Token.
 */
function bugCreateError(failure: HttpErrorResponse, boardUrl: string | null): TrackerError {
  return mapBugCreateError(failure, boardUrl) ?? { key: bugErrorKey(failure), params: {}, accountLink: false };
}

function bugErrorKey(failure: HttpErrorResponse): string {
  switch (failure.status) {
    case 501:
      return 'testResults.detail.bug.notSupported';
    case 409:
      return 'testResults.detail.bug.alreadyLinked';
    case 412:
      return (failure.error as { code?: string } | null)?.code === 'NO_ACTIVE_INTEGRATION'
        ? 'testResults.detail.bug.noIntegration'
        : 'testResults.detail.bug.tokenMissing';
    default:
      return 'testResults.detail.bug.createError';
  }
}
